import {Flex, Image, Text} from 'exsportia-components'
import {MenuItemComplexProps} from 'exsportia-components/dist/components/organisms/menu-item'
import React, {FC, useMemo} from 'react'
import {useNavigate, useParams} from 'react-router-dom'

import {useVenue} from '../../api/venue/hooks'
import {VenueGroupMember} from '../../api/venue/types'
import {DropdownMenu} from '../../ui/components/dropdown-menu/dropdown-menu'
import {useLocale} from '../../utils/i18n'
import {DEFAULT_VENUE_IMAGE} from '../../utils/remote-content'

// Organizations v1 (WBA-6). A venue that is, or belongs to, a group gets a small chip next to its
// name in the header: the group's logo and a chevron. Opening it lists the group and every member,
// the current one marked; one tap switches. Venues in no group render nothing. (The first version
// was a bar of pills under the header — Dima 2026-09-07: not obvious enough.)

const Row: FC<{member: VenueGroupMember; isCurrent: boolean; isGroup: boolean}> = ({member, isCurrent, isGroup}) => (
  <Flex alignItems='center' gap='10px' py='2px'>
    <Image width='28px' height='28px' borderRadius='50%' aspectRatio={1} size='cover' src={member.logo ?? DEFAULT_VENUE_IMAGE} />
    <Text settings={{fontSize: '14px', fontWeight: isCurrent ? 700 : 500, whiteSpace: 'nowrap'}}>
      {member.name || member.uid}
    </Text>
    {isGroup ? <Text settings={{fontSize: '11px', color: '#00103D99', whiteSpace: 'nowrap'}}>·</Text> : null}
    {isCurrent ? <Text settings={{fontSize: '12px', color: '#ED3163', whiteSpace: 'nowrap'}}>✓</Text> : null}
  </Flex>
)

export const VenueGroupSwitcher: FC = () => {
  const {l} = useLocale()
  const navigate = useNavigate()
  const {venueId} = useParams<{venueId: string}>()
  const {data: venueData} = useVenue()
  const group = venueData?.venueGroup
  const options = useMemo<MenuItemComplexProps[]>(() => {
    if (!group) {
 return []
}
    const members: VenueGroupMember[] = [group, ...group.children]
    return members.map((member) => ({
      name: member.uid,
      text: <Row member={member} isCurrent={member.uid === venueId} isGroup={member.uid === group.uid} />,
      disabled: member.uid === venueId,
      onClick: () => navigate(`/${member.uid}`),
    }))
  }, [group, venueId, navigate])
  if (!group || !venueId) {
    return null
  }
  return (
    <DropdownMenu name='venue-group-switcher' height='max-content' options={options}>
      <Flex
        alignItems='center'
        gap='6px'
        px='8px'
        py='4px'
        ml='12px'
        borderRadius='20px'
        border='1px solid #BFC1C7'
        bg='white'
        cursor='pointer'
        flexShrink={0}
        title={`${l('venueGroup_partOf')} ${group.name || ''}`}
        aria-label={`${l('venueGroup_partOf')} ${group.name || ''}`}
      >
        <Image width='24px' height='24px' borderRadius='50%' aspectRatio={1} size='cover' src={group.logo ?? DEFAULT_VENUE_IMAGE} />
        <Text settings={{fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap', color: '#00103D'}}>
          {group.children.length + 1}
        </Text>
        <Text settings={{fontSize: '10px', color: '#00103D99'}}>▼</Text>
      </Flex>
    </DropdownMenu>
  )
}
